import React, { useState } from "react";
import styled from "styled-components";

// Styled Components with proper prop filtering
const DropdownCard = styled.div.withConfig({
  shouldForwardProp: (prop) => !["grow", "isOpen"].includes(prop),
})`
  margin-bottom: 0.25rem !important;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
  border: 1px solid rgba(0, 0, 0, 0.125);
  border-radius: 0.25rem;
`;

const DropdownHeader = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "isOpen",
})<{ $isOpen: boolean }>`
  background-color: #f8f9fa;
  transition: background-color 0.2s ease;
  cursor: pointer;
  padding: 0.75rem 1.25rem;
  margin-bottom: 0;
  border-bottom: ${(props) =>
    props.$isOpen ? "1px solid rgba(0, 0, 0, 0.125)" : "none"};
  border-top-left-radius: calc(0.25rem - 1px);
  border-top-right-radius: calc(0.25rem - 1px);
  border-bottom-left-radius: ${(props) =>
    props.$isOpen ? "0" : "calc(0.25rem - 1px)"};
  border-bottom-right-radius: ${(props) =>
    props.$isOpen ? "0" : "calc(0.25rem - 1px)"};

  &:hover {
    background-color: #e9ecef;
  }

  &:active {
    background-color: #dee2e6;
  }
`;

const HeaderContent = styled.div`
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
`;

const CardTitle = styled.h6`
  margin-bottom: 0 !important;
  font-size: 1rem;
  font-weight: 500;
  color: #495057;
`;

const ChevronIcon = styled.i.withConfig({
  shouldForwardProp: (prop) => prop !== "isOpen",
})<{ $isOpen: boolean }>`
  font-size: 18px;
  transition: transform 0.3s ease;
  transform: ${(props) => (props.$isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  color: #6c757d;
`;

const DropdownBody = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "isOpen",
})<{ $isOpen: boolean }>`
  max-height: ${(props) => (props.$isOpen ? "500px" : "0")};
  overflow: hidden;
  transition: max-height 0.3s ease, padding 0.3s ease;
  padding: ${(props) => (props.$isOpen ? "1rem 1.25rem" : "0 1.25rem")};
  background-color: #ffffff;
  border-bottom-left-radius: calc(0.25rem - 1px);
  border-bottom-right-radius: calc(0.25rem - 1px);
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f8f9fa;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:first-child {
    padding-top: 0;
  }
`;

const DetailLabel = styled.div`
  font-size: 14px;
  color: #6c757d;
  font-weight: 400;
`;

const DetailValue = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #495057;
`;

// Enhanced Props interface with optional grow prop
interface CollapsibleDropdownProps {
  agentDeduction: string;
  title?: string;
  grow?: boolean | number; // Add grow prop to handle it properly
  className?: string;
  // Add other common props that might be passed from parent components
  style?: React.CSSProperties;
}

// Main Component with proper prop handling
const CollapsibleDropdown: React.FC<CollapsibleDropdownProps> = ({
  agentDeduction,
  title = "Additional Details",
  grow, // Accept but don't use the grow prop
  className,
  style,
  ...otherProps // Capture any other props to prevent them from being passed to DOM
}) => {
  // Internal state for collapsible dropdown
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  // Internal toggle function
  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  return (
    <DropdownCard
      className={`card ${className || ""}`}
      style={style}
      // Don't spread otherProps to prevent unknown props from reaching DOM
    >
      <DropdownHeader $isOpen={isDropdownOpen} onClick={toggleDropdown}>
        <HeaderContent>
          <CardTitle>{title}</CardTitle>
          <ChevronIcon
            $isOpen={isDropdownOpen}
            className={`mdi ${
              isDropdownOpen ? "mdi-chevron-up" : "mdi-chevron-down"
            }`}
          />
        </HeaderContent>
      </DropdownHeader>

      <DropdownBody $isOpen={isDropdownOpen}>
        <DetailRow>
          <DetailLabel>Agent Deduction:</DetailLabel>
          <DetailValue>{agentDeduction || "N/A"}</DetailValue>
        </DetailRow>
      </DropdownBody>
    </DropdownCard>
  );
};

export default CollapsibleDropdown;
